import React, { useEffect, useMemo, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import FontSize from '@tiptap/extension-font-size';
import Placeholder from '@tiptap/extension-placeholder';
import classnames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaLink,
  FaHighlighter,
  FaRemoveFormat,
  FaFont,
  FaTextHeight,
} from 'react-icons/fa';

// Helper to get computed CSS variable value from _root.scss
const getCssVar = (varName: string): string => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
};

// Highlight color palette - uses CSS variables from _root.scss
const getHighlightColors = () => [
  { color: getCssVar('--warning-400'), label: 'Yellow' },
  { color: getCssVar('--error-400'), label: 'Red' },
  { color: getCssVar('--tech-blue-400'), label: 'Blue' },
  { color: getCssVar('--success-400'), label: 'Green' },
  { color: getCssVar('--organizations-orchid'), label: 'Purple' },
  { color: getCssVar('--warning-500'), label: 'Orange' },
];

// Text color palette - uses CSS variables from _root.scss
// Note: White removed as it often exports poorly
const getTextColors = () => [
  { color: getCssVar('--warning-400'), label: 'Yellow' },
  { color: getCssVar('--error-400'), label: 'Red' },
  { color: getCssVar('--tech-blue-400'), label: 'Blue' },
  { color: getCssVar('--success-400'), label: 'Green' },
  { color: getCssVar('--organizations-orchid'), label: 'Purple' },
  { color: getCssVar('--warning-500'), label: 'Orange' },
];

// Font size options - relative sizes for better UX
const fontSizeOptions = [
  { value: '0.875rem', label: 'Small' },
  { value: '1rem', label: 'Normal' },
  { value: '1.25rem', label: 'Large' },
  { value: '1.5rem', label: 'X-Large' },
];

interface TiptapRichTextEditorProps {
  value: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minHeight?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  showToolbar?: boolean;
}

export const TiptapRichTextEditor: React.FC<TiptapRichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  disabled = false,
  className,
  minHeight = '150px',
  autoFocus = false,
  readOnly = false,
  showToolbar = true,
}) => {
  const [showHighlightMenu, setShowHighlightMenu] = useState(false);
  const [showColorMenu, setShowColorMenu] = useState(false);
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // Compute colors from CSS variables once on mount
  const highlightColors = useMemo(() => getHighlightColors(), []);
  const textColors = useMemo(() => getTextColors(), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        // Disabled here because they are configured separately below
        link: false,
        underline: false,
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      TextStyle,
      Color,
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: !readOnly,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    editable: !disabled && !readOnly,
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor }) => {
      if (onChange) {
        const html = editor.getHTML();
        onChange(html);
      }
    },
  });

  // Update editor content when value prop changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled && !readOnly);
    }
  }, [disabled, readOnly, editor]);

  if (!editor) {
    return null;
  }

  const handleSetLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    }
    setShowLinkDialog(false);
    setLinkUrl('');
  };

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setShowLinkDialog(false);
  };

  const openLinkDialog = () => {
    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl || '');
    setShowLinkDialog(true);
  };

  const renderTooltip = (text: string) => (
    <Tooltip id={`tooltip-${text.toLowerCase().replace(/\s/g, '-')}`}>
      {text}
    </Tooltip>
  );

  return (
    <div
      className={classnames('tiptap-editor', className, {
        'tiptap-editor--disabled': disabled,
        'tiptap-editor--readonly': readOnly,
      })}
      style={readOnly ? undefined : { minHeight }}
    >
      {/* Toolbar - only show in edit mode */}
      {showToolbar && !readOnly && (
        <div className="tiptap-editor__toolbar">
          {/* Text Formatting Group */}
          <div className="tiptap-editor__toolbar-group">
            <OverlayTrigger placement="top" overlay={renderTooltip('Bold')}>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleBold().run()}
                disabled={!editor.can().chain().focus().toggleBold().run()}
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active': editor.isActive('bold'),
                })}
                aria-label="Bold"
              >
                <FaBold />
              </button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={renderTooltip('Italic')}>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                disabled={!editor.can().chain().focus().toggleItalic().run()}
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active': editor.isActive('italic'),
                })}
                aria-label="Italic"
              >
                <FaItalic />
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip('Underline')}
            >
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                disabled={!editor.can().chain().focus().toggleUnderline().run()}
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active': editor.isActive('underline'),
                })}
                aria-label="Underline"
              >
                <FaUnderline />
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip('Strikethrough')}
            >
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                disabled={!editor.can().chain().focus().toggleStrike().run()}
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active': editor.isActive('strike'),
                })}
                aria-label="Strikethrough"
              >
                <FaStrikethrough />
              </button>
            </OverlayTrigger>
          </div>

          {/* Highlighting Group */}
          <div className="tiptap-editor__toolbar-group">
            <div className="tiptap-editor__highlight-wrapper">
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Highlight')}
              >
                <button
                  type="button"
                  onClick={() => setShowHighlightMenu(!showHighlightMenu)}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active':
                      editor.isActive('highlight'),
                  })}
                  aria-label="Highlight"
                >
                  <FaHighlighter />
                </button>
              </OverlayTrigger>
              {showHighlightMenu && (
                <div className="tiptap-editor__color-dropdown">
                  {highlightColors.map(({ color, label }) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => {
                        editor.chain().focus().setHighlight({ color }).run();
                        setShowHighlightMenu(false);
                      }}
                      className={classnames('tiptap-editor__color-swatch', {
                        'tiptap-editor__color-swatch--active': editor.isActive(
                          'highlight',
                          { color }
                        ),
                      })}
                      style={{ backgroundColor: color }}
                      title={label}
                      aria-label={`Highlight ${label}`}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().unsetHighlight().run();
                      setShowHighlightMenu(false);
                    }}
                    className="tiptap-editor__button tiptap-editor__button--clear"
                    title="Remove Highlight"
                    aria-label="Remove Highlight"
                  >
                    <FaRemoveFormat />
                  </button>
                </div>
              )}
            </div>

            {/* Text Color */}
            <div className="tiptap-editor__highlight-wrapper">
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Text Color')}
              >
                <button
                  type="button"
                  onClick={() => setShowColorMenu(!showColorMenu)}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active':
                      editor.isActive('textStyle'),
                  })}
                  aria-label="Text Color"
                >
                  <FaFont />
                </button>
              </OverlayTrigger>
              {showColorMenu && (
                <div className="tiptap-editor__color-dropdown">
                  {textColors.map(({ color, label }) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => {
                        editor.chain().focus().setColor(color).run();
                        setShowColorMenu(false);
                      }}
                      className={classnames('tiptap-editor__color-swatch', {
                        'tiptap-editor__color-swatch--active': editor.isActive(
                          'textStyle',
                          { color }
                        ),
                      })}
                      style={{ backgroundColor: color }}
                      title={label}
                      aria-label={`Text Color ${label}`}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().unsetColor().run();
                      setShowColorMenu(false);
                    }}
                    className="tiptap-editor__button tiptap-editor__button--clear"
                    title="Remove Color"
                    aria-label="Remove Color"
                  >
                    <FaRemoveFormat />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Font Size Group */}
          <div className="tiptap-editor__toolbar-group">
            <div className="tiptap-editor__highlight-wrapper">
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Font Size')}
              >
                <button
                  type="button"
                  onClick={() => setShowFontSizeMenu(!showFontSizeMenu)}
                  className="tiptap-editor__button"
                  aria-label="Font Size"
                >
                  <FaTextHeight />
                </button>
              </OverlayTrigger>
              {showFontSizeMenu && (
                <div className="tiptap-editor__font-size-dropdown">
                  {fontSizeOptions.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        editor.chain().focus().setFontSize(value).run();
                        setShowFontSizeMenu(false);
                      }}
                      className={classnames('tiptap-editor__font-size-option', {
                        'tiptap-editor__font-size-option--active':
                          editor.getAttributes('textStyle').fontSize === value,
                      })}
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().unsetFontSize().run();
                      setShowFontSizeMenu(false);
                    }}
                    className="tiptap-editor__font-size-option tiptap-editor__font-size-option--reset"
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Lists & Alignment Group */}
          <div className="tiptap-editor__toolbar-group">
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip('Bullet List')}
            >
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active':
                    editor.isActive('bulletList'),
                })}
                aria-label="Bullet List"
              >
                <FaListUl />
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip('Numbered List')}
            >
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active':
                    editor.isActive('orderedList'),
                })}
                aria-label="Numbered List"
              >
                <FaListOl />
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip('Align Left')}
            >
              <button
                type="button"
                onClick={() =>
                  editor.chain().focus().setTextAlign('left').run()
                }
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active': editor.isActive({
                    textAlign: 'left',
                  }),
                })}
                aria-label="Align Left"
              >
                <FaAlignLeft />
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip('Align Center')}
            >
              <button
                type="button"
                onClick={() =>
                  editor.chain().focus().setTextAlign('center').run()
                }
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active': editor.isActive({
                    textAlign: 'center',
                  }),
                })}
                aria-label="Align Center"
              >
                <FaAlignCenter />
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip('Align Right')}
            >
              <button
                type="button"
                onClick={() =>
                  editor.chain().focus().setTextAlign('right').run()
                }
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active': editor.isActive({
                    textAlign: 'right',
                  }),
                })}
                aria-label="Align Right"
              >
                <FaAlignRight />
              </button>
            </OverlayTrigger>
          </div>

          {/* Links & Actions Group */}
          <div className="tiptap-editor__toolbar-group">
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip('Insert Link')}
            >
              <button
                type="button"
                onClick={openLinkDialog}
                className={classnames('tiptap-editor__button', {
                  'tiptap-editor__button--active': editor.isActive('link'),
                })}
                aria-label="Insert Link"
              >
                <FaLink />
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip('Clear Formatting')}
            >
              <button
                type="button"
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .unsetColor()
                    .unsetHighlight()
                    .clearNodes()
                    .unsetAllMarks()
                    .run()
                }
                className="tiptap-editor__button"
                aria-label="Clear Formatting"
              >
                <FaRemoveFormat />
              </button>
            </OverlayTrigger>
          </div>
        </div>
      )}

      {/* Editor Content */}
      <div className="tiptap-editor__content" style={readOnly ? undefined : { minHeight }}>
        <EditorContent editor={editor} />
      </div>

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="tiptap-editor__link-dialog">
          <div className="tiptap-editor__link-dialog-content">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="Enter URL"
              className="form-control"
              autoFocus
            />
            <div className="tiptap-editor__link-dialog-actions">
              <button
                type="button"
                onClick={handleSetLink}
                className="btn btn-primary btn-sm"
              >
                Set Link
              </button>
              {editor.isActive('link') && (
                <button
                  type="button"
                  onClick={handleRemoveLink}
                  className="btn btn-danger btn-sm"
                >
                  Remove Link
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowLinkDialog(false)}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

TiptapRichTextEditor.displayName = 'TiptapRichTextEditor';

export default TiptapRichTextEditor;
